import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Occasion gifting builder. One occasion (radio) plus a quantity per candle; the whole
 * selection goes to the cart in a single request, with the occasion stored as line item
 * properties. Products, variants, prices and stock are the store's own.
 *
 * Properties written on every line:
 * - the public packaging label (shown in cart and checkout), value = occasion title
 * - `_belora_order_source` = `occasions` (hidden in the storefront, visible in the admin)
 * - `_belora_collection` = the packaging collection code, for example `noor-al-emarat`
 */
class BeloraOccasions extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** Guards against a second request while one is in flight. */
  #busy = false;

  /** Kept in the small-screen bar after an add, so the button that had focus stays on the page. */
  #note = '';

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @returns {HTMLInputElement | null} The checked occasion radio. */
  get #occasion() {
    const radio = this.querySelector('.belora-occ__radio:checked');
    return radio instanceof HTMLInputElement ? radio : null;
  }

  /** @returns {{ id: string, title: string, price: number, quantity: number }[]} Selected candles. */
  get #selection() {
    const lines = [];

    for (const item of this.querySelectorAll('[data-item]')) {
      if (!(item instanceof HTMLElement)) continue;
      const input = item.querySelector('[data-quantity]');
      const quantity = input instanceof HTMLInputElement ? this.#clamp(item, Number(input.value)) : 0;
      if (quantity < 1) continue;

      lines.push({
        id: item.dataset.variantId ?? '',
        title: item.dataset.title ?? '',
        price: Number(item.dataset.price) || 0,
        quantity,
      });
    }

    return lines;
  }

  /**
   * @param {HTMLElement} item
   * @param {number} value
   * @returns {number} A whole quantity between zero and the stock limit, when there is one.
   */
  #clamp(item, value) {
    const max = item.dataset.max === undefined ? Infinity : Number(item.dataset.max);
    if (!Number.isFinite(value) || value < 0) return 0;
    return Math.min(Math.floor(value), max);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('.belora-occ__radio')) {
      this.#render();
      this.#revealBuilder();
      return;
    }

    if (target.matches('[data-quantity]')) {
      this.#setQuantity(target, Number(target.value));
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const step = event.target.closest('[data-step]');
    if (step instanceof HTMLButtonElement) {
      const input = step.closest('[data-item]')?.querySelector('[data-quantity]');
      if (input instanceof HTMLInputElement) {
        this.#setQuantity(input, Number(input.value) + Number(step.dataset.step));
      }
      return;
    }

    if (event.target.closest('[data-change]')) {
      event.preventDefault();
      const radio = this.#occasion ?? this.querySelector('.belora-occ__radio');
      if (radio instanceof HTMLInputElement) {
        radio.closest('.belora-occ__card')?.scrollIntoView({ behavior: this.#scrollBehavior, block: 'center' });
        radio.focus({ preventScroll: true });
      }
      return;
    }

    if (event.target.closest('[data-add]')) {
      this.#add();
    }
  };

  /**
   * @param {{ items?: { variant_id: number, quantity: number, properties?: Record<string, string> | null }[] }} cart
   * @param {string} variantId
   * @param {string} code
   * @returns {number} How many of a variant the cart holds for one occasion.
   */
  #inCart(cart, variantId, code) {
    return (cart.items ?? [])
      .filter((item) => String(item.variant_id) === variantId && item.properties?._belora_collection === code)
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  get #scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /**
   * @param {HTMLInputElement} input
   * @param {number} value
   */
  #setQuantity(input, value) {
    const item = input.closest('[data-item]');
    if (!(item instanceof HTMLElement)) return;

    const quantity = this.#clamp(item, value);
    const limited = Number.isFinite(value) && value > quantity;
    input.value = String(quantity);
    this.#note = '';
    this.#render();

    if (limited) {
      this.#announce(
        (this.dataset.statusMax ?? '').replace('[count]', String(quantity)).replace('[title]', item.dataset.title ?? ''),
        'error'
      );
    }
  }

  /** Moves the shopper on to the candle list once an occasion is chosen. */
  #revealBuilder() {
    const builder = this.querySelector('[data-builder]');
    if (!(builder instanceof HTMLElement)) return;
    builder.scrollIntoView({ behavior: this.#scrollBehavior, block: 'start' });
  }

  /**
   * @param {number} cents
   * @returns {string} The amount in the shop's money format.
   */
  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const withDecimals = (cents / 100).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const noDecimals = Math.round(cents / 100).toLocaleString('en');

    return format
      .replace(/\{\{\s*amount_no_decimals[a-z_]*\s*\}\}/, noDecimals)
      .replace(/\{\{\s*amount[a-z_]*\s*\}\}/, withDecimals)
      .replace(/<[^>]+>/g, '');
  }

  /** Redraws the summary, the stepper states and the add button from the current inputs. */
  #render() {
    const occasion = this.#occasion;
    const lines = this.#selection;
    const count = lines.reduce((sum, line) => sum + line.quantity, 0);
    const total = lines.reduce((sum, line) => sum + line.quantity * line.price, 0);

    for (const item of this.querySelectorAll('[data-item]')) {
      if (!(item instanceof HTMLElement)) continue;
      const input = item.querySelector('[data-quantity]');
      const value = input instanceof HTMLInputElement ? Number(input.value) : 0;
      const minus = item.querySelector('[data-step="-1"]');
      const plus = item.querySelector('[data-step="1"]');
      if (minus instanceof HTMLButtonElement) minus.disabled = value <= 0;
      if (plus instanceof HTMLButtonElement) plus.disabled = item.dataset.max !== undefined && value >= Number(item.dataset.max);
    }

    const chosen = this.querySelector('[data-chosen]');
    const change = this.querySelector('[data-change]');
    if (occasion && chosen) chosen.textContent = occasion.dataset.title ?? '';
    if (change instanceof HTMLElement) change.hidden = !occasion;

    const heading = this.querySelector('[data-summary-heading]');
    if (heading && occasion) {
      heading.textContent = (this.dataset.summaryHeading ?? '').replace('[occasion]', occasion.dataset.title ?? '');
    }

    const list = this.querySelector('[data-lines]');
    if (list) {
      list.replaceChildren(
        ...lines.map((line) => {
          const row = document.createElement('li');
          row.className = 'belora-occ__line';
          const name = document.createElement('span');
          name.textContent = `${line.title} × ${line.quantity}`;
          const amount = document.createElement('span');
          amount.textContent = this.#money(line.price * line.quantity);
          row.append(name, amount);
          return row;
        })
      );
    }

    const countText = count === 1 ? this.dataset.countOne ?? '' : (this.dataset.countOther ?? '').replace('[count]', String(count));
    const empty = this.querySelector('[data-empty]');
    const countRow = this.querySelector('[data-count]');
    const totalRow = this.querySelector('[data-total-row]');
    const totalValue = this.querySelector('[data-total]');
    const hint = this.querySelector('[data-hint]');
    const bar = this.querySelector('[data-bar]');
    const barText = this.querySelector('[data-bar-text]');

    if (empty instanceof HTMLElement) empty.hidden = count > 0;
    if (countRow instanceof HTMLElement) {
      countRow.hidden = count === 0;
      countRow.textContent = countText;
    }
    if (totalRow instanceof HTMLElement) totalRow.hidden = count === 0;
    if (totalValue) totalValue.textContent = this.#money(total);
    if (hint instanceof HTMLElement) hint.hidden = Boolean(occasion) && count > 0;
    for (const add of this.querySelectorAll('[data-add]')) {
      add.setAttribute('aria-disabled', String(this.#busy || !occasion || count === 0));
    }
    if (bar instanceof HTMLElement) bar.hidden = count === 0 && this.#note === '';
    if (barText) barText.textContent = count > 0 ? `${countText} · ${this.#money(total)}` : this.#note;

    if (count > 0) {
      this.#announce(
        (this.dataset.statusTotal ?? '').replace('[count]', countText).replace('[total]', this.#money(total))
      );
    } else {
      this.#announce('');
    }
  }

  /**
   * @param {string} message
   * @param {'info' | 'error'} [state]
   */
  #announce(message, state = 'info') {
    const status = this.querySelector('[data-status]');
    if (!(status instanceof HTMLElement)) return;
    status.dataset.state = state;
    status.textContent = message;
  }

  /** Adds every selected candle in one request, then lets the theme's cart drawer take over. */
  async #add() {
    const occasion = this.#occasion;
    const lines = this.#selection;
    if (this.#busy || !occasion || lines.length === 0) return;

    this.#busy = true;
    this.#render();

    const properties = {
      [this.dataset.propertyLabel || 'Packaging collection']: occasion.dataset.title ?? '',
      _belora_order_source: 'occasions',
      _belora_collection: occasion.value,
    };

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: line.id, quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const root = window.Shopify?.routes?.root ?? '/';

      // Quantities already in the cart for this occasion, to measure what this request adds
      const cartBefore = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      const before = new Map(lines.map((line) => [line.id, this.#inCart(cartBefore, line.id, occasion.value)]));

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lines.map((line) => ({ id: Number(line.id), quantity: line.quantity, properties })),
          sections: sectionIds.join(','),
        }),
      });
      const result = await response.json();
      const refused = !response.ok || Boolean(result.status);

      // Whatever Shopify answered, read the cart back: when stock changed after the page
      // loaded it can add fewer than requested and still report an error.
      const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      const added = lines.map((line) => ({
        ...line,
        added: Math.max(0, this.#inCart(cart, line.id, occasion.value) - (before.get(line.id) ?? 0)),
      }));
      const addedCount = added.reduce((sum, line) => sum + line.added, 0);

      let sections = result.sections;
      if (refused && addedCount > 0 && sectionIds.length > 0) {
        sections = await (await fetch(`${root}?sections=${sectionIds.join(',')}`)).json();
      }

      if (addedCount > 0) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'belora-occasions',
            sourceId: this.id,
            itemCount: addedCount,
            sections,
            didError: false,
          },
        });
      } else {
        deferred.reject(new Error(result.description || result.message || ''));
      }

      // Clear what reached the cart so it cannot be added twice; a candle that came up
      // short has no more stock to give, so its control is closed.
      for (const line of added) {
        const item = this.querySelector(`[data-item][data-variant-id="${line.id}"]`);
        const input = item?.querySelector('[data-quantity]');
        if (!(item instanceof HTMLElement) || !(input instanceof HTMLInputElement)) continue;
        if (line.added > 0) input.value = '0';
        if (line.added < line.quantity) {
          item.dataset.max = '0';
          input.max = '0';
          input.value = '0';
        }
      }

      this.#busy = false;

      if (!refused) {
        this.#note = this.dataset.statusAdded ?? '';
        this.#render();
        this.#announce(this.#note);
        return;
      }

      const shortfall = added
        .filter((line) => line.added < line.quantity)
        .map((line) =>
          (this.dataset.statusPartial ?? '')
            .replace('[added]', String(line.added))
            .replace('[requested]', String(line.quantity))
            .replace('[title]', line.title)
        );
      const message = [result.description || result.message || this.dataset.statusError || '', ...shortfall]
        .filter(Boolean)
        .join(' ');

      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      this.#note = addedCount > 0 ? message : '';
      this.#render();
      this.#announce(message, 'error');
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : this.dataset.statusError ?? '';
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      this.#busy = false;
      this.#render();
      this.#announce(message, 'error');
    }
  }
}

if (!customElements.get('belora-occasions')) customElements.define('belora-occasions', BeloraOccasions);
